import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Search, ArrowRight } from 'lucide-react';
import { callAdminGetVetaxisOperations } from '../../services/admin';

type Clinic = {
  id: string;
  name: string;
  ownerId: string | null;
  subscriptionPlanId: string;
  baseSeatLimit: number;
  activeMemberCount: number;
};

type OpsData = {
  clinics: Clinic[];
  clinicUserCounts: Record<string, number>;
};

const AdminVetAxisClinics: React.FC = () => {
  const [ops, setOps] = useState<OpsData>({ clinics: [], clinicUserCounts: {} });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await callAdminGetVetaxisOperations({ limitClinics: 500, limitUsers: 2000 });
        if (!mounted) return;
        setOps({ clinics: data.clinics || [], clinicUserCounts: data.clinicUserCounts || {} });
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || 'Failed to load clinics');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredClinics = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ops.clinics;
    return ops.clinics.filter((clinic) =>
      [clinic.id, clinic.name, clinic.ownerId, clinic.subscriptionPlanId]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [ops.clinics, search]);

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin/vetaxis" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to VetAxis Ops
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <Building2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">VetAxis Clinics</h1>
              <p className="text-slate-600 dark:text-gray-400">Select a clinic to view all linked users</p>
            </div>
          </div>
        </motion.div>

        <motion.div className="glass-effect rounded-2xl p-4 shadow-glass mb-6" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clinics by name, ID, owner, or plan"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </motion.div>

        {error && <div className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</div>}

        {loading ? (
          <div className="glass-effect rounded-2xl p-8 text-center text-slate-600 dark:text-gray-400">Loading clinics...</div>
        ) : filteredClinics.length === 0 ? (
          <div className="glass-effect rounded-2xl p-8 text-center text-slate-600 dark:text-gray-400">No clinics found.</div>
        ) : (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {filteredClinics.map((clinic) => (
              <Link key={clinic.id} to={`/admin/vetaxis/clinics/${encodeURIComponent(clinic.id)}`}>
                <motion.div whileHover={{ y: -4 }} className="glass-effect glass-effect-hover rounded-2xl p-5 shadow-glass h-full">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{clinic.name}</h2>
                      <p className="font-mono text-xs text-slate-500 dark:text-gray-400">{clinic.id}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </div>
                  <div className="space-y-1 text-sm text-slate-600 dark:text-gray-300">
                    <p>Plan: <span className="capitalize">{clinic.subscriptionPlanId}</span></p>
                    <p>Linked users: {ops.clinicUserCounts[clinic.id] ?? clinic.activeMemberCount ?? 0}</p>
                    <p>Owner UID: <span className="font-mono text-xs">{clinic.ownerId || '—'}</span></p>
                    <p>Base seat limit: {clinic.baseSeatLimit > 0 ? clinic.baseSeatLimit : 'Unlimited'}</p>
                  </div>
                </motion.div>
              </Link>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminVetAxisClinics;
